// Redaction v2 (#483): which of the PDF's own marks (its annotations, not Visiodocs' markups) a redacted copy keeps.
// One list for the viewer's redaction mode and the redact-pdf service, so the page the lawyer marks is the page that
// is painted. Spec: docs/superpowers/specs/2026-10-18-redaction-v2-design.md, "What is drawn into the picture".
//
// Both sides render with pdf.js 5.3.31, forms drawn into the page (AnnotationMode.ENABLE_STORAGE), and hide a removed
// mark by setting `noView: true` for its id in the document's annotationStorage. Never set `noView: false`: pdf.js lets
// a stored noView override the mark's own Hidden flag, which would draw a mark the PDF hides.
// The viewer also has to hide a removed mark's HTML element: a sticky note without a stored appearance is an HTML icon.

/** The fields of a pdf.js annotation (page.getAnnotations()) the decision reads. */
export interface PdfMarkData {
    subtype: string;
    pushButton?: boolean;
    hasAppearance?: boolean;
}

/** Marks that only point at something hidden: the icon goes with what it points to. */
export const REMOVED_PDF_MARK_SUBTYPES: readonly string[] = [
    'Text',             // sticky note icon; its popup text is never on the page
    'Popup',
    'FileAttachment',   // paperclip of an attached file
    'Link',             // the box or border; the linked text is page content and stays
    'Sound',
    'Movie',
    'Screen',
    'RichMedia',
];

/**
 * Whether a redacted copy leaves this mark out. Everything else is drawn as pdf.js draws it for display: highlights,
 * ink, shapes, lines, free text, stamps and form values; marks flagged Hidden or NoView never are.
 * A free-text box saved without its own appearance is left out too (Henrik, 2026-10-19): pdf.js can build its
 * appearance only in a browser, so the service could not draw what the viewer shows. Count it in the notice.
 *
 * @param {PdfMarkData} mark the annotation as pdf.js reports it
 * @return {boolean} true when the redacted copy leaves it out
 */
export const isRemovedFromRedactedCopy = (mark: PdfMarkData): boolean =>
    REMOVED_PDF_MARK_SUBTYPES.includes(mark.subtype)
    || (mark.subtype === 'Widget' && mark.pushButton === true)
    || (mark.subtype === 'FreeText' && !mark.hasAppearance);
